import { getSupplierTypes, SUPPLIER_TYPES } from '../../utils/supplierTypes';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { analyticsAPI, suppliersAPI, supplierDealsAPI } from '../../services/api';
import { useDataSource } from '../../contexts/DataSourceContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import {
  UsersIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardBody, CardHeader, Badge, Spinner, Skeleton } from '../ui';
import {
  TrendingUp,
  Users,
  Target,
  Trophy,
  Star,
  Award,
  Zap,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  GitCompare,
  UserPlus,
  Package,
  ShoppingCart,
  Truck,
  Beaker,
  LineChart as LineChartIcon,
  Brain,
  FileText,
  Download,
  Database,
} from 'lucide-react';

// Import animated chart components from sections
import {
  SalesmenPerformanceWheel,
  AdvancedPerformerAnalytics,
  Animated3DPieChart,
  AnimatedRadarChart,
  AnimatedFunnelChart,
  ClientAnalyticsGrid,
  LiquidFillChart,
  GaugeCluster,
  MetricComparison,
  AnimatedStatCard,
  HeatmapGrid,
  TimelineChart,
  ProductPerformanceMatrix,
} from '../charts';

// Mock data for moved components - defined before component to avoid hoisting issues
const mockSalesmenData = [
  { name: 'John Smith', revenue: 450000, clients: 45, deals: 32, performance: 92, rank: 1 },
  { name: 'Sarah Johnson', revenue: 420000, clients: 38, deals: 28, performance: 88, rank: 2 },
  { name: 'Mike Davis', revenue: 280000, clients: 28, deals: 22, performance: 75, rank: 3 },
  { name: 'Emily Brown', revenue: 350000, clients: 35, deals: 25, performance: 82, rank: 4 },
  { name: 'David Wilson', revenue: 310000, clients: 30, deals: 24, performance: 78, rank: 5 },
  { name: 'Lisa Anderson', revenue: 380000, clients: 40, deals: 27, performance: 85, rank: 6 },
];

const mockPerformersData = [
  { label: 'John Smith', percentage: 92, color: '#3B82F6', actual: 92, target: 100, sublabel: 'Top Performer' },
  { label: 'Sarah Johnson', percentage: 88, color: '#10B981', actual: 88, target: 100, sublabel: 'High Achiever' },
  { label: 'Mike Davis', percentage: 75, color: '#F59E0B', actual: 75, target: 100, sublabel: 'Solid Contributor' },
  { label: 'Emily Brown', percentage: 82, color: '#8B5CF6', actual: 82, target: 100, sublabel: 'Rising Star' },
  { label: 'David Wilson', percentage: 78, color: '#EC4899', actual: 78, target: 100, sublabel: 'Consistent' },
  { label: 'Lisa Anderson', percentage: 85, color: '#14B8A6', actual: 85, target: 100, sublabel: 'Excellence' },
];

const mockComparisonData = [
  { label: 'Enterprise Sales', percentage: 88, color: '#3B82F6', actual: 450, target: 100, sublabel: '88%' },
  { label: 'SMB Sales', percentage: 75, color: '#10B981', actual: 320, target: 100, sublabel: '75%' },
  { label: 'Channel Partners', percentage: 82, color: '#8B5CF6', actual: 280, target: 100, sublabel: '82%' },
  { label: 'Direct Sales', percentage: 65, color: '#F59E0B', actual: 210, target: 100, sublabel: '65%' },
];

const mockAcquisitionData = [
  { label: 'Leads Generated', percentage: 100, color: '#3B82F6', actual: 1200, target: 1200, sublabel: '1,200 Leads' },
  { label: 'Qualified Prospects', percentage: 75, color: '#10B981', actual: 900, target: 1200, sublabel: '900 Qualified' },
  { label: 'Active Negotiations', percentage: 50, color: '#F59E0B', actual: 600, target: 1200, sublabel: '600 Negotiating' },
  { label: 'Closed Deals', percentage: 30, color: '#8B5CF6', actual: 360, target: 1200, sublabel: '360 Closed' },
];

const mockClientsList = [
  { id: 1, name: 'Acme Corporation', type: 'Company', importance: 'High' },
  { id: 2, name: 'TechStart Inc', type: 'Company', importance: 'High' },
  { id: 3, name: 'Global Solutions', type: 'Company', importance: 'Medium' },
  { id: 4, name: 'John Doe Enterprises', type: 'Individual', importance: 'Medium' },
  { id: 5, name: 'Innovation Labs', type: 'Company', importance: 'High' },
  { id: 6, name: 'Digital Dynamics', type: 'Company', importance: 'Low' },
  { id: 7, name: 'Future Systems', type: 'Company', importance: 'Medium' },
  { id: 8, name: 'Smart Solutions', type: 'Company', importance: 'High' },
  { id: 9, name: 'Jane Smith LLC', type: 'Individual', importance: 'Low' },
];

const mockProductsList = [
  { name: 'Premium Widget Pro', category: 'Electronics', revenue: 450000, units: 1200, growth: 15.5, rating: 5 },
  { name: 'Standard Widget', category: 'Electronics', revenue: 320000, units: 2400, growth: 8.2, rating: 4 },
  { name: 'Budget Widget Lite', category: 'Electronics', revenue: 180000, units: 3600, growth: -2.1, rating: 3 },
  { name: 'Enterprise Solution', category: 'Software', revenue: 680000, units: 450, growth: 22.3, rating: 5 },
  { name: 'Professional Suite', category: 'Software', revenue: 420000, units: 890, growth: 12.7, rating: 4 },
  { name: 'Starter Package', category: 'Software', revenue: 150000, units: 1800, growth: 5.4, rating: 4 },
  { name: 'Premium Service', category: 'Services', revenue: 290000, units: 680, growth: 18.9, rating: 5 },
  { name: 'Basic Service', category: 'Services', revenue: 120000, units: 1200, growth: 3.2, rating: 3 },
];

const mockChartsData = [
  { label: 'Chart Views', percentage: 85, color: '#3B82F6', actual: 850, target: 1000, sublabel: '850 / 1000' },
  { label: 'Data Points', percentage: 92, color: '#10B981', actual: 920, target: 1000, sublabel: '920 / 1000' },
];

const mockPredictiveData = [
  { label: 'Forecast Accuracy', percentage: 88, color: '#8B5CF6', actual: 88, target: 100, sublabel: '88%' },
  { label: 'Trend Confidence', percentage: 18, color: '#EF4444', actual: 18, target: 100, sublabel: '18%' },
];

const AdminAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDataSource();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [overview, setOverview] = useState<any>(null);
  const [salesmanPerformance, setSalesmanPerformance] = useState<any[]>([]);
  const [dealsByPeriod, setDealsByPeriod] = useState<any[]>([]);
  const [clientsByPeriod, setClientsByPeriod] = useState<any[]>([]);
  const [revenueTrends, setRevenueTrends] = useState<any[]>([]);
  const [topPerformers, setTopPerformers] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);

  // Real data state
  const [salesmenData, setSalesmenData] = useState<any[]>(mockSalesmenData);
  const [performersData, setPerformersData] = useState<any[]>(mockPerformersData);
  const [acquisitionData, setAcquisitionData] = useState<any[]>(mockAcquisitionData);
  const [clientsList, setClientsList] = useState<any[]>(mockClientsList);
  const [productsList, setProductsList] = useState<any[]>(mockProductsList);

  // Active section for scrolling
  const [activeSection, setActiveSection] = useState<string>('overview');
  const location = useLocation();

  // Handle hash navigation for deep linking
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.slice(1);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [location.hash, loading]);

  const periods = [
    { value: '1d', label: '1 Day' },
    { value: '7d', label: '7 Days' },
    { value: '1m', label: '1 Month' },
    { value: '1y', label: '1 Year' },
  ];

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  useEffect(() => {
    fetchData();
  }, [selectedPeriod, isDemoMode]);

  useEffect(() => {
    // Listen for data source changes
    const handleDataSourceChange = () => {
      fetchData();
    };

    window.addEventListener('dataSourceChanged', handleDataSourceChange);
    return () => {
      window.removeEventListener('dataSourceChanged', handleDataSourceChange);
    };
  }, [selectedPeriod, isDemoMode]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (isDemoMode) {
        // Use mock data
        setSalesmenData(mockSalesmenData);
        setPerformersData(mockPerformersData);
        setAcquisitionData(mockAcquisitionData);
        setClientsList(mockClientsList);
        setProductsList(mockProductsList);
      } else {
        // Fetch real data
        const [
          teamPerformanceData,
          acquisitionFunnelData,
          clientsListData,
          productsListData,
        ] = await Promise.all([
          analyticsAPI.getTeamPerformanceMetrics(selectedPeriod),
          analyticsAPI.getClientAcquisitionFunnel(selectedPeriod),
          analyticsAPI.getClientList(selectedPeriod),
          analyticsAPI.getProductPerformanceList(selectedPeriod),
        ]);

        setSalesmenData(teamPerformanceData);
        // Convert team performance to performers format
        const performersFormatted = teamPerformanceData.map((salesman: any) => ({
          label: salesman.name,
          percentage: salesman.performance,
          color: colors[salesman.rank - 1] || '#3B82F6',
          actual: salesman.performance,
          target: 100,
          sublabel: salesman.performance >= 90 ? 'Top Performer' : salesman.performance >= 80 ? 'High Achiever' : 'Solid Contributor',
        }));
        setPerformersData(performersFormatted);
        setAcquisitionData(acquisitionFunnelData);
        setClientsList(clientsListData);
        setProductsList(productsListData);
      }

      const [
        overviewData,
        salesmanData,
        dealsData,
        clientsData,
        revenueData,
        topPerformersData,
        suppliersData,
        supplierDealsData,
      ] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getSalesmanPerformance(selectedPeriod),
        analyticsAPI.getDealsByPeriod(selectedPeriod),
        analyticsAPI.getClientsByPeriod(selectedPeriod),
        analyticsAPI.getRevenueTrends(selectedPeriod),
        analyticsAPI.getTopPerformers(selectedPeriod),
        suppliersAPI.getAll({ limit: 100 }),
        supplierDealsAPI.getAll(),
      ]);

      setOverview(overviewData);
      setSalesmanPerformance(salesmanData);
      setDealsByPeriod(dealsData);
      setClientsByPeriod(clientsData);
      setRevenueTrends(revenueData);
      setTopPerformers(topPerformersData);

      // Calculate supplier statistics
      const totalSuppliers = suppliersData.pagination?.total || 0;
      const totalSpent = supplierDealsData.reduce((sum: number, deal: any) => sum + parseFloat(deal.amount), 0);
      const avgDealSize = supplierDealsData.length > 0 ? totalSpent / supplierDealsData.length : 0;

      const suppliersByType = suppliersData.data?.reduce((acc: any, supplier: any) => {
        getSupplierTypes(supplier).forEach(type => { acc[type] = (acc[type] || 0) + 1; });
        return acc;
      }, {});

      setSupplierStats({
        total: totalSuppliers,
        totalSpent,
        avgDealSize,
        totalDeals: supplierDealsData.length,
        byType: suppliersByType,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatCompactCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const getSupplierTypeData = () => {
    if (!supplierStats?.byType) return [];
    return Object.entries(supplierStats.byType).map(([type, count]) => ({
      name: SUPPLIER_TYPES.find(option => option.value === type)?.label || type.replace(/_/g, ' '),
      value: count,
    }));
  };

  const getSalesmanRadarData = () => {
    if (!salesmanPerformance || salesmanPerformance.length === 0) return [];
    return salesmanPerformance.slice(0, 5).map((salesman: any) => ({
      salesman: salesman.name,
      deals: salesman.dealsCount || 0,
      revenue: parseFloat(salesman.totalRevenue || 0) / 1000,
      clients: salesman.clientsCount || 0,
    }));
  };

  const getClientGrowthData = () => {
    if (!clientsByPeriod || clientsByPeriod.length === 0) return [];
    return clientsByPeriod.map((item: any, index: number) => ({
      ...item,
      growth: index > 0 ? item.count - clientsByPeriod[index - 1].count : 0,
    }));
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 min-h-screen">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 rounded-2xl" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <p className="text-sm text-error-600">{error}</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 min-h-screen">
      {/* Enhanced Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 relative overflow-hidden"
      >
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 bg-white/10 rounded-full"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.1, 0.4, 0.1],
              }}
              transition={{
                duration: 4 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.4,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-4">
            <motion.button
              onClick={() => navigate('/admin')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6 text-white" />
            </motion.button>
            <div>
              <h1 className="text-4xl font-bold text-white flex items-center gap-3">
                <Trophy className="w-10 h-10" />
                Salesman Performance Analytics
              </h1>
              <p className="mt-2 text-indigo-100">
                Comprehensive insights into team performance, revenue trends, and client acquisition
              </p>
            </div>
          </div>

          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl p-1.5 border border-white/20">
            <CalendarIcon className="h-5 w-5 text-white ml-3" />
            <div className="flex gap-1">
              {periods.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setSelectedPeriod(period.value)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${selectedPeriod === period.value
                    ? 'bg-white text-indigo-600 shadow-lg'
                    : 'text-white hover:bg-white/20'
                    }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Cards */}
      <motion.div
        id="key-metrics"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="scroll-mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
      >
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 overflow-hidden shadow-xl rounded-2xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-sm font-medium uppercase tracking-wide">Total Revenue</p>
                <p className="text-4xl font-bold text-white mt-2">{formatCompactCurrency(overview?.totalRevenue || 0)}</p>
                <p className="text-primary-100 text-xs mt-1">{formatCurrency(overview?.totalRevenue || 0)}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <CurrencyDollarIcon className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 px-6 py-3">
            <p className="text-primary-100 text-xs">All time revenue</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-success-500 to-success-600 overflow-hidden shadow-xl rounded-2xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-success-100 text-sm font-medium uppercase tracking-wide">Total Clients</p>
                <p className="text-4xl font-bold text-white mt-2">{overview?.totalClients || 0}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <BuildingOfficeIcon className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 px-6 py-3">
            <p className="text-success-100 text-xs">In your database</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 overflow-hidden shadow-xl rounded-2xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-secondary-100 text-sm font-medium uppercase tracking-wide">Active Salesmen</p>
                <p className="text-4xl font-bold text-white mt-2">{overview?.totalSalesmen || 0}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <UsersIcon className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 px-6 py-3">
            <p className="text-secondary-100 text-xs">Active team members</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-warning-500 to-warning-600 overflow-hidden shadow-xl rounded-2xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-warning-100 text-sm font-medium uppercase tracking-wide">Supplier Expenses</p>
                <p className="text-4xl font-bold text-white mt-2">{formatCompactCurrency(supplierStats?.totalSpent || 0)}</p>
                <p className="text-warning-100 text-xs mt-1">{formatCurrency(supplierStats?.totalSpent || 0)}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                <TruckIcon className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 px-6 py-3">
            <p className="text-warning-100 text-xs">Total supplier costs</p>
          </div>
        </div>
      </motion.div>

      {/* ===== SALESMAN PERFORMANCE SECTION ===== */}
      <motion.div
        id="salesman-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="scroll-mt-24 mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center shadow-lg">
            <Award className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Salesman Performance Overview</h2>
            <p className="text-gray-500">Comprehensive analytics for your highest-performing team members</p>
          </div>
        </div>

        {/* Performance Wheel */}
        <div className="mb-8">
          <SalesmenPerformanceWheel
            title="Team Performance Overview"
            salesmen={salesmenData}
          />
        </div>

        {/* Advanced Analytics */}
        <AdvancedPerformerAnalytics
          title="Performance Analytics Dashboard"
          data={performersData.map(m => ({
            label: m.label,
            value: Math.round(m.percentage),
            sublabel: m.sublabel,
          }))}
        />
      </motion.div>

      {/* ===== PERFORMANCE DISTRIBUTION ===== */}
      <motion.div
        id="performance-comparison"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="scroll-mt-24 mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
            <GitCompare className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Performance Distribution</h2>
            <p className="text-gray-500">Visual breakdown of sales team performance</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Animated3DPieChart
            title="Sales Distribution by Team"
            data={mockComparisonData.map(m => ({
              label: m.label,
              value: m.actual,
              color: m.color,
              icon: <Star size={16} />,
            }))}
          />

          <AnimatedRadarChart
            title="Multi-Dimensional Performance"
            data={mockComparisonData.map(m => ({
              label: m.label,
              value: m.actual,
              color: m.color,
              maxValue: 500,
            }))}
          />
        </div>
      </motion.div>

      {/* ===== CLIENT ACQUISITION FUNNEL ===== */}
      <motion.div
        id="client-analytics"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="scroll-mt-24 mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
            <UserPlus className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Client Acquisition</h2>
            <p className="text-gray-500">Track the complete sales funnel and conversion rates</p>
          </div>
        </div>

        <AnimatedFunnelChart
          title="Sales Funnel Analysis"
          data={acquisitionData.map((m, index) => {
            const icons = [
              <Users className="w-7 h-7 text-white" />,
              <Target className="w-7 h-7 text-white" />,
              <GitCompare className="w-7 h-7 text-white" />,
              <Trophy className="w-7 h-7 text-white" />,
            ];
            return {
              label: m.label,
              value: m.actual,
              color: m.color,
              icon: icons[index],
              sublabel: m.sublabel,
            };
          })}
        />
      </motion.div>

      {/* ===== CLIENT ANALYTICS ===== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg">
            <Users className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Client Analytics</h2>
            <p className="text-gray-500">Comprehensive client data and performance insights</p>
          </div>
        </div>

        <ClientAnalyticsGrid
          title="Client Portfolio Overview"
          clients={clientsList}
        />
      </motion.div>

      {/* ===== REVENUE TRENDS & CLIENT GROWTH ===== */}
      <motion.div
        id="revenue-charts"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
      >
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-5 border-b border-primary-200">
            <h3 className="text-xl font-bold text-neutral-900 flex items-center">
              <span className="w-3 h-3 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 mr-3"></span>
              Revenue Trends
            </h3>
            <p className="text-sm text-neutral-600 mt-1">Track revenue over time</p>
          </div>
          <div className="p-6">
            {revenueTrends && revenueTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueTrends}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    formatter={(value: any) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Area type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-neutral-400">
                No revenue data for this period
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-success-50 to-success-100 px-6 py-5 border-b border-success-200">
            <h3 className="text-xl font-bold text-neutral-900 flex items-center">
              <span className="w-3 h-3 rounded-full bg-gradient-to-r from-success-500 to-success-600 mr-3"></span>
              Client Growth
            </h3>
            <p className="text-sm text-neutral-600 mt-1">Client acquisition trends</p>
          </div>
          <div className="p-6">
            {clientsByPeriod && clientsByPeriod.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={getClientGrowthData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis yAxisId="left" stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="count" stroke="#10B981" name="Total Clients" strokeWidth={3} dot={{ fill: '#10B981', r: 5 }} activeDot={{ r: 7 }} />
                  <Line yAxisId="right" type="monotone" dataKey="growth" stroke="#F59E0B" name="Growth" strokeWidth={3} dot={{ fill: '#F59E0B', r: 5 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-neutral-400">
                No client data for this period
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ===== PRODUCT PERFORMANCE ===== */}
      <motion.div
        id="product-analytics"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="scroll-mt-24 mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg">
            <ShoppingCart className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Product Performance</h2>
            <p className="text-gray-500">Comprehensive product analytics with multiple visualization modes</p>
          </div>
        </div>

        <ProductPerformanceMatrix
          title="Product Analytics Dashboard"
          products={productsList}
        />
      </motion.div>

      {/* ===== ADVANCED CHARTS & PREDICTIONS ===== */}
      <motion.div
        id="advanced-analytics"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="scroll-mt-24 mb-12"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Brain className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Advanced Analytics</h2>
            <p className="text-gray-500">AI-powered insights and predictive analytics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GaugeCluster
            title="Prediction Confidence"
            gauges={mockPredictiveData.map(m => ({
              label: m.label,
              value: m.actual,
              max: m.target,
              color: m.color,
            }))}
          />

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Model Performance</h3>
            <div className="grid grid-cols-2 gap-6">
              {mockPredictiveData.slice(0, 2).map((metric, index) => (
                <div key={index} className="flex justify-center">
                  <LiquidFillChart
                    percentage={metric.percentage}
                    size={160}
                    color={metric.color}
                    label={metric.label}
                    sublabel={metric.sublabel}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ===== SALESMAN PERFORMANCE COMPARISON ===== */}
      <motion.div
        id="supplier-data"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
      >
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 px-6 py-5 border-b border-secondary-200">
            <h3 className="text-xl font-bold text-neutral-900 flex items-center">
              <span className="w-3 h-3 rounded-full bg-gradient-to-r from-secondary-500 to-secondary-600 mr-3"></span>
              Top Salesmen Performance
            </h3>
            <p className="text-sm text-neutral-600 mt-1">Multi-dimensional performance view</p>
          </div>
          <div className="p-6">
            {getSalesmanRadarData().length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={getSalesmanRadarData()}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="salesman" stroke="#6b7280" />
                  <PolarRadiusAxis stroke="#6b7280" />
                  <Radar name="Deals" dataKey="deals" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                  <Radar name="Revenue (K)" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
                  <Legend />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[350px] text-neutral-400">
                No salesman performance data available
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-warning-50 to-warning-100 px-6 py-5 border-b border-warning-200">
            <h3 className="text-xl font-bold text-neutral-900 flex items-center">
              <span className="w-3 h-3 rounded-full bg-gradient-to-r from-warning-500 to-warning-600 mr-3"></span>
              Supplier Distribution by Type
            </h3>
            <p className="text-sm text-neutral-600 mt-1">Suppliers can belong to more than one category</p>
          </div>
          <div className="p-6">
            {getSupplierTypeData().length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={getSupplierTypeData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getSupplierTypeData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[350px] text-neutral-400">
                No supplier data available
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ===== DEALS & REVENUE COMPARISON ===== */}
      <motion.div
        id="top-performers"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12"
      >
        <Card variant="bordered" hoverable>
          <CardHeader title="Deals Over Time" />
          <CardBody>
            {dealsByPeriod && dealsByPeriod.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dealsByPeriod}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                  <Legend />
                  <Bar dataKey="count" fill="#3B82F6" name="Deals Closed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-neutral-400">
                No deals data for this period
              </div>
            )}
          </CardBody>
        </Card>

        <Card variant="bordered" hoverable>
          <CardHeader title="Salesman Revenue Comparison" />
          <CardBody>
            {salesmanPerformance && salesmanPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesmanPerformance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis dataKey="name" type="category" width={100} stroke="#6b7280" />
                  <Tooltip formatter={(value: any) => formatCurrency(value)} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                  <Bar dataKey="totalRevenue" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-neutral-400">
                No salesman performance data for this period
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* ===== TOP PERFORMERS TABLES ===== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <Card variant="bordered" hoverable>
          <CardHeader title="Top Performers by Deals" />
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Salesman</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Deals</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {topPerformers?.topByDeals && topPerformers.topByDeals.length > 0 ? (
                    topPerformers.topByDeals.map((performer: any, index: number) => (
                      <tr key={index} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'neutral'}>
                            #{index + 1}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">{performer.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="success">{performer.dealsCount}</Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-sm text-neutral-400">
                        No data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        <Card variant="bordered" hoverable>
          <CardHeader title="Top Performers by Revenue" />
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Salesman</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {topPerformers?.topByRevenue && topPerformers.topByRevenue.length > 0 ? (
                    topPerformers.topByRevenue.map((performer: any, index: number) => (
                      <tr key={index} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'neutral'}>
                            #{index + 1}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">{performer.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="success">{formatCurrency(parseFloat(performer.totalRevenue))}</Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-sm text-neutral-400">
                        No data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminAnalytics;
